import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './components/Toast';
import Navbar from './components/Navbar';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import PendingApproval from './pages/PendingApproval';
import PendingUsers from './pages/PendingUsers';
import OnlineUsers from './pages/OnlineUsers';
import AdminDashboard from './pages/AdminDashboard';
import AdminQuizzes from './pages/AdminQuizzes';
import IssuesManagement from './components/IssuesManagement';
import FeedbackManagement from './components/FeedbackManagement';
import MyFeedback from './pages/MyFeedback';
import Support from './pages/Support';
import UserDashboard from './pages/UserDashboard';
import MyQuizzes from './pages/MyQuizzes';
import QuizAttempts from './pages/QuizAttempts';
import Leaderboard from './pages/Leaderboard';
import Students from './pages/Students';
import StudentProfile from './pages/StudentProfile';
import AllQuestions from './pages/AllQuestions';
import Groups from './pages/Groups';
import GroupDetails from './pages/GroupDetails';
import GroupLectureAssignments from './pages/GroupLectureAssignments';
import QuizDetails from './pages/QuizDetails';
import QuizResults from './pages/QuizResults';
import Drive from './pages/Drive';
import QuestionBank from './pages/QuestionBank';
import QuestionDetails from './pages/QuestionDetails';
import Interviews from './pages/Interviews';
import ScheduleInterview from './pages/ScheduleInterview';
import MyInterviews from './pages/MyInterviews';
import TakeInterview from './pages/TakeInterview';
import InterviewResponses from './pages/InterviewResponses';
import InterviewLeaderboard from './pages/InterviewLeaderboard';
import InterviewStatistics from './pages/InterviewStatistics';
import InterviewDetails from './pages/InterviewDetails';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Resumes from './pages/Resumes';
import ResumeBuilder from './components/ResumeBuilder';
import ResumePreview from './pages/ResumePreview';
import ManageFlashcards from './components/ManageFlashcards';
import ViewFlashcards from './components/ViewFlashcards';
import FlashcardDetail from './pages/FlashcardDetail';
import FlashcardMatch from './pages/FlashcardMatch';
import Courses from './pages/Courses';
import CreateCourse from './pages/CreateCourse';
import CourseBuilder from './pages/CourseBuilder';
import CourseSettings from './pages/CourseSettings';
import StudentCourseProgress from './pages/StudentCourseProgress';
import ChapterProgressList from './pages/ChapterProgressList';
import ChapterProgressMatrix from './pages/ChapterProgressMatrix';
import CourseContent from './pages/CourseContent';
import MyCourses from './pages/MyCourses';
import AssetLibrary from './pages/AssetLibrary';
import TakeQuizById from './pages/TakeQuizById';
import CodingAssignments from './pages/CodingAssignments';
import MyCodingAssignments from './pages/MyCodingAssignments';
import TakeCodingAssignment from './components/TakeCodingAssignment';
import AssignmentDetails from './pages/AssignmentDetails';
import AssignmentSubmissions from './pages/AssignmentSubmissions';
import AIInstructionTemplates from './pages/AIInstructionTemplates';
import CodeRepos from './pages/CodeRepos';
import CodeRepoBuilder from './pages/CodeRepoBuilder';
import MyCodeRepos from './pages/MyCodeRepos';
import CodeRepoDetail from './pages/CodeRepoDetail';
import CodeRepoProgressList from './pages/CodeRepoProgressList';
import CodeRepoProgressMatrix from './pages/CodeRepoProgressMatrix';
import StudentRepoProgress from './pages/StudentRepoProgress';
import FeeManagement from './pages/FeeManagement';
import FeeDashboard from './pages/FeeDashboard';
import PendingFeesReminder from './pages/PendingFeesReminder';
import BankAccountManagement from './pages/BankAccountManagement';
import BankAccountDetails from './pages/BankAccountDetails';
import CodeSnippets from './pages/CodeSnippets';
import CodeSnippetView from './pages/CodeSnippetView';
import AccessCodeSnippet from './pages/AccessCodeSnippet';
import LiveCodeEditor from './pages/LiveCodeEditor';
import PublicEnquiryLayout from './components/PublicEnquiryLayout';
import EnquiryFormPage from './pages/EnquiryFormPage';
import EnquiryThankYouPage from './pages/EnquiryThankYouPage';
import EnquiryListPage from './pages/admin/EnquiryListPage';
import EnquiryDetailPage from './pages/admin/EnquiryDetailPage';
import './index.css';

function PrivateRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Check if user is pending approval
  if (user.role === 'user' && !user.isApproved) {
    return <Navigate to="/pending-approval" />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/user" />;
  }

  return children;
}

function AppRoutes() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isPublicEnquiry =
    location.pathname === '/enquiry' || location.pathname === '/enquiry/thank-you';

  // Listen for logout events from axios interceptor
  useEffect(() => {
    const handleLogoutEvent = (event) => {
      logout();
      navigate('/login', { replace: true });
    };

    window.addEventListener('auth:logout', handleLogoutEvent);

    return () => {
      window.removeEventListener('auth:logout', handleLogoutEvent);
    };
  }, [logout, navigate]);

  return (
    <div
      className={isPublicEnquiry ? 'public-enquiry-shell' : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      {!isPublicEnquiry && <Navbar />}
      <div className={isPublicEnquiry ? 'public-enquiry-shell-main' : undefined} style={{ flex: 1 }}>
        <Routes>
        <Route element={<PublicEnquiryLayout />}>
          <Route path="/enquiry" element={<EnquiryFormPage />} />
          <Route path="/enquiry/thank-you" element={<EnquiryThankYouPage />} />
        </Route>
        <Route 
          path="/" 
          element={
            user ? (
              user.role === 'admin' ? 
                <Navigate to="/admin" /> : 
                user.isApproved ? 
                  <Navigate to="/user" /> :
                  <Navigate to="/pending-approval" />
            ) : (
              <Navigate to="/login" />
            )
          } 
        />
        <Route 
          path="/login" 
          element={user ? <Navigate to={user.role === 'admin' ? '/admin' : (user.isApproved ? '/user' : '/pending-approval')} /> : <Login />} 
        />
        <Route 
          path="/register" 
          element={user ? <Navigate to={user.role === 'admin' ? '/admin' : (user.isApproved ? '/user' : '/pending-approval')} /> : <Register />} 
        />
        <Route 
          path="/forgot-password" 
          element={user ? <Navigate to={user.role === 'admin' ? '/admin' : (user.isApproved ? '/user' : '/pending-approval')} /> : <ForgotPassword />} 
        />
        <Route 
          path="/reset-password/:token" 
          element={user ? <Navigate to={user.role === 'admin' ? '/admin' : (user.isApproved ? '/user' : '/pending-approval')} /> : <ResetPassword />} 
        />
        <Route 
          path="/verify-email/:token" 
          element={<VerifyEmail />} 
        />
        <Route
          path="/pending-approval"
          element={user && user.role === 'user' && !user.isApproved ? <PendingApproval /> : <Navigate to="/login" />}
        />
        <Route
          path="/admin"
          element={
            <PrivateRoute adminOnly>
              <AdminDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/quizzes"
          element={
            <PrivateRoute adminOnly>
              <AdminQuizzes />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/quiz/:quizId"
          element={
            <PrivateRoute adminOnly>
              <QuizDetails />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/issues"
          element={
            <PrivateRoute adminOnly>
              <IssuesManagement />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/feedback"
          element={
            <PrivateRoute adminOnly>
              <FeedbackManagement />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/enquiries"
          element={
            <PrivateRoute adminOnly>
              <EnquiryListPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/enquiries/:id"
          element={
            <PrivateRoute adminOnly>
              <EnquiryDetailPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/fee-management"
          element={
            <PrivateRoute adminOnly>
              <FeeManagement />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/fee-dashboard"
          element={
            <PrivateRoute adminOnly>
              <FeeDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/pending-fees"
          element={
            <PrivateRoute adminOnly>
              <PendingFeesReminder />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/bank-accounts"
          element={
            <PrivateRoute adminOnly>
              <BankAccountManagement />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/bank-accounts/:id"
          element={
            <PrivateRoute adminOnly>
              <BankAccountDetails />
            </PrivateRoute>
          }
        />
        <Route
          path="/user"
          element={
            <PrivateRoute>
              <UserDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/my-feedback"
          element={
            <PrivateRoute>
              <MyFeedback />
            </PrivateRoute>
          }
        />
        <Route
          path="/support"
          element={
            <PrivateRoute>
              <Support />
            </PrivateRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <PrivateRoute>
              <Profile />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <PrivateRoute adminOnly>
              <Settings />
            </PrivateRoute>
          }
        />
        <Route
          path="/my-quizzes"
          element={
            <PrivateRoute>
              <MyQuizzes />
            </PrivateRoute>
          }
        />
        <Route
          path="/my-quizzes/:quizId/attempts"
          element={
            <PrivateRoute>
              <QuizAttempts />
            </PrivateRoute>
          }
        />
        <Route
          path="/quiz-results"
          element={
            <PrivateRoute>
              <QuizResults />
            </PrivateRoute>
          }
        />
        <Route
          path="/quiz/:quizId"
          element={
            <PrivateRoute>
              <TakeQuizById />
            </PrivateRoute>
          }
        />
        <Route
          path="/students"
          element={
            <PrivateRoute adminOnly>
              <Students />
            </PrivateRoute>
          }
        />
        <Route
          path="/pending-users"
          element={
            <PrivateRoute adminOnly>
              <PendingUsers />
            </PrivateRoute>
          }
        />
        <Route
          path="/online-users"
          element={
            <PrivateRoute adminOnly>
              <OnlineUsers />
            </PrivateRoute>
          }
        />
        <Route
          path="/students/:studentId"
          element={
            <PrivateRoute adminOnly>
              <StudentProfile />
            </PrivateRoute>
          }
        />
        <Route
          path="/groups"
          element={
            <PrivateRoute adminOnly>
              <Groups />
            </PrivateRoute>
          }
        />
        <Route
          path="/groups/:groupId"
          element={
            <PrivateRoute adminOnly>
              <GroupDetails />
            </PrivateRoute>
          }
        />
        <Route
          path="/groups/:groupId/lecture-assignments"
          element={
            <PrivateRoute adminOnly>
              <GroupLectureAssignments />
            </PrivateRoute>
          }
        />
        <Route
          path="/questions"
          element={
            <PrivateRoute adminOnly>
              <AllQuestions />
            </PrivateRoute>
          }
        />
        <Route
          path="/question-bank"
          element={
            <PrivateRoute>
              <QuestionBank />
            </PrivateRoute>
          }
        />
        <Route
          path="/question-bank/:id"
          element={
            <PrivateRoute>
              <QuestionDetails />
            </PrivateRoute>
          }
        />
        <Route
          path="/interviews"
          element={
            <PrivateRoute adminOnly>
              <Interviews />
            </PrivateRoute>
          }
        />
        <Route
          path="/schedule-interview"
          element={
            <PrivateRoute adminOnly>
              <ScheduleInterview />
            </PrivateRoute>
          }
        />
        <Route
          path="/edit-interview/:id"
          element={
            <PrivateRoute adminOnly>
              <ScheduleInterview />
            </PrivateRoute>
          }
        />
        <Route          path="/edit-interview/:id"
          element={
            <PrivateRoute adminOnly>
              <ScheduleInterview />
            </PrivateRoute>
          }
        />
        <Route          path="/interview/:id"
          element={
            <PrivateRoute>
              <TakeInterview />
            </PrivateRoute>
          }
        />
        <Route
          path="/interview/:id/responses"
          element={
            <PrivateRoute adminOnly>
              <InterviewResponses />
            </PrivateRoute>
          }
        />
        <Route
          path="/interview/:id/leaderboard"
          element={
            <PrivateRoute adminOnly>
              <InterviewLeaderboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/interview/:id/statistics"
          element={
            <PrivateRoute adminOnly>
              <InterviewStatistics />
            </PrivateRoute>
          }
        />
        <Route
          path="/my-interviews"
          element={
            <PrivateRoute>
              <MyInterviews />
            </PrivateRoute>
          }
        />
        <Route
          path="/interview-details/:id"
          element={
            <PrivateRoute>
              <InterviewDetails />
            </PrivateRoute>
          }
        />
        <Route
          path="/take-interview/:id"
          element={
            <PrivateRoute>
              <TakeInterview />
            </PrivateRoute>
          }
        />
        <Route
          path="/leaderboard"
          element={
            <PrivateRoute>
              <Leaderboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/drive"
          element={
            <PrivateRoute adminOnly>
              <Drive />
            </PrivateRoute>
          }
        />
        <Route
          path="/resumes"
          element={
            <PrivateRoute>
              <Resumes />
            </PrivateRoute>
          }
        />
        <Route
          path="/resumes/create"
          element={
            <PrivateRoute>
              <ResumeBuilder />
            </PrivateRoute>
          }
        />
        <Route
          path="/resumes/edit/:id"
          element={
            <PrivateRoute>
              <ResumeBuilder />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/flashcards"
          element={
            <PrivateRoute adminOnly>
              <ManageFlashcards />
            </PrivateRoute>
          }
        />
        <Route
          path="/flashcards"
          element={
            <PrivateRoute>
              <ViewFlashcards />
            </PrivateRoute>
          }
        />
        <Route
          path="/flashcards/:id/study"
          element={
            <PrivateRoute>
              <FlashcardDetail />
            </PrivateRoute>
          }
        />
        <Route
          path="/flashcards/:id/match"
          element={
            <PrivateRoute>
              <FlashcardMatch />
            </PrivateRoute>
          }
        />
        <Route
          path="/courses"
          element={
            <PrivateRoute adminOnly>
              <Courses />
            </PrivateRoute>
          }
        />
        <Route
          path="/courses/create"
          element={
            <PrivateRoute adminOnly>
              <CreateCourse />
            </PrivateRoute>
          }
        />
        <Route
          path="/asset-library"
          element={
            <PrivateRoute adminOnly>
              <AssetLibrary />
            </PrivateRoute>
          }
        />
        <Route
          path="/course-builder/:id"
          element={
            <PrivateRoute adminOnly>
              <CourseBuilder />
            </PrivateRoute>
          }
        />
        <Route
          path="/course-settings/:id"
          element={
            <PrivateRoute adminOnly>
              <CourseSettings />
            </PrivateRoute>
          }
        />
        <Route
          path="/course/:courseId/student/:studentId/progress"
          element={
            <PrivateRoute adminOnly>
              <StudentCourseProgress />
            </PrivateRoute>
          }
        />
        <Route
          path="/courses/:courseId/chapters/progress"
          element={
            <PrivateRoute adminOnly>
              <ChapterProgressList />
            </PrivateRoute>
          }
        />
        <Route
          path="/courses/:courseId/chapters/:chapterId/progress-matrix"
          element={
            <PrivateRoute adminOnly>
              <ChapterProgressMatrix />
            </PrivateRoute>
          }
        />
        <Route
          path="/course/:id"
          element={
            <PrivateRoute>
              <CourseContent />
            </PrivateRoute>
          }
        />
        <Route
          path="/my-courses"
          element={
            <PrivateRoute>
              <MyCourses />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/coding-assignments"
          element={
            <PrivateRoute adminOnly>
              <CodingAssignments />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/ai-instruction-templates"
          element={
            <PrivateRoute adminOnly>
              <AIInstructionTemplates />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/code-repos"
          element={
            <PrivateRoute adminOnly>
              <CodeRepos />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/code-repo-builder/:id"
          element={
            <PrivateRoute adminOnly>
              <CodeRepoBuilder />
            </PrivateRoute>
          }
        />
        <Route
          path="/code-repos/:repoId/progress"
          element={
            <PrivateRoute adminOnly>
              <CodeRepoProgressList />
            </PrivateRoute>
          }
        />
        <Route
          path="/code-repos/:repoId/progress-matrix"
          element={
            <PrivateRoute adminOnly>
              <CodeRepoProgressMatrix />
            </PrivateRoute>
          }
        />
        <Route
          path="/code-repos/:repoId/student/:studentId/progress"
          element={
            <PrivateRoute adminOnly>
              <StudentRepoProgress />
            </PrivateRoute>
          }
        />
        <Route
          path="/my-coding-assignments"
          element={
            <PrivateRoute>
              <MyCodingAssignments />
            </PrivateRoute>
          }
        />
        <Route
          path="/my-code-repos"
          element={
            <PrivateRoute>
              <MyCodeRepos />
            </PrivateRoute>
          }
        />
        <Route
          path="/my-code-repos/:repoCode"
          element={
            <PrivateRoute>
              <CodeRepoDetail />
            </PrivateRoute>
          }
        />
        <Route
          path="/coding-assignment/:code"
          element={
            <PrivateRoute>
              <TakeCodingAssignment />
            </PrivateRoute>
          }
        />
        <Route
          path="/assignment-details/:id"
          element={
            <PrivateRoute>
              <AssignmentDetails />
            </PrivateRoute>
          }
        />
        <Route
          path="/assignment-submissions/:id"
          element={
            <PrivateRoute adminOnly>
              <AssignmentSubmissions />
            </PrivateRoute>
          }
        />
        <Route
          path="/code-snippets"
          element={
            <PrivateRoute adminOnly>
              <CodeSnippets />
            </PrivateRoute>
          }
        />
        <Route
          path="/live-code-editor"
          element={
            <PrivateRoute adminOnly>
              <LiveCodeEditor />
            </PrivateRoute>
          }
        />
        <Route
          path="/access-code"
          element={
            <PrivateRoute>
              <AccessCodeSnippet />
            </PrivateRoute>
          }
        />
        <Route
          path="/code-snippet/view/:id"
          element={
            <PrivateRoute>
              <CodeSnippetView />
            </PrivateRoute>
          }
        />
        <Route
          path="/code-snippets/:id"
          element={
            <PrivateRoute adminOnly>
              <CodeSnippetView />
            </PrivateRoute>
          }
        />
        <Route
          path="/resumes/preview/:id"
          element={
            <PrivateRoute>
              <ResumePreview />
            </PrivateRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <ToastProvider>
          <AppRoutes />
        </ToastProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
